'use client';

import {
  useCallback,
  useEffect,
  useState,
  useRef,
  MouseEventHandler,
} from 'react';
import { clsx } from 'clsx';

type RangeValue = Array<string | number> | undefined;
type OnChangeRange = (value: RangeValue) => void;

export type SliderProps = {
  min: number;
  max: number;
  value: RangeValue;
  onChange: OnChangeRange;
  onMouseUp?: MouseEventHandler<HTMLInputElement>;
  step?: number;
  className?: string;
};

export const Slider = ({
  min,
  max,
  onChange,
  onMouseUp,
  value,
  step,
  className,
}: SliderProps) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const minValRef = useRef(min);
  const maxValRef = useRef(max);
  const range = useRef<HTMLDivElement>(null);

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, getPercent]);

  useEffect(() => {
    const minPercent = getPercent(minValRef.current);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, getPercent]);

  const onChangeMinValue = (value: number | string) => {
    const numberValue = Number(value);

    setMinVal(numberValue);
    minValRef.current = numberValue;
  };

  const onChangeMaxValue = (value: number | string) => {
    const numberValue = Number(value);

    setMaxVal(numberValue);
    maxValRef.current = numberValue;
  };

  useEffect(() => {
    if (value) {
      onChangeMinValue(value[0]);
      onChangeMaxValue(value[1]);
    }
  }, [value, onChange]);

  return (
    <div
      className={clsx(
        'range-slider flex items-center justify-center w-full relative',
        className
      )}
    >
      <input
        type='range'
        min={min}
        max={max}
        value={minVal}
        step={step}
        onMouseUp={onMouseUp}
        onChange={(event) => {
          const value = Math.min(Number(event.target.value), maxVal - 1);

          onChangeMinValue(value);

          onChange([value, maxVal]);
        }}
        className='range-slider__thumb pointer-events-none absolute h-0 w-full outline-hidden z-30'
      />
      <input
        type='range'
        min={min}
        max={max}
        value={maxVal}
        step={step}
        onMouseUp={onMouseUp}
        onChange={(event) => {
          const value = Math.max(Number(event.target.value), minVal + 1);

          onChangeMaxValue(value);

          onChange([minVal, value]);
        }}
        className='range-slider__thumb pointer-events-none absolute h-0 w-full outline-hidden z-40'
      />

      <div className='relative w-full'>
        <div className='range-slider__track w-full z-1 rounded-sm h-1 absolute' />
        <div
          ref={range}
          className='range-slider__range rounded-sm h-1 absolute z-20'
        />
      </div>
    </div>
  );
};
